import { useState, type FormEvent } from 'react';
import { MessageSquare, Pencil, Reply, Send, Trash2 } from 'lucide-react';
import type { TaskComment, UserSummary } from '@ssn/shared';
import { LIMITS } from '@ssn/shared';
import { Avatar } from '@/components/ui/Avatar';
import { Button, IconButton } from '@/components/ui/Button';
import { TextArea } from '@/components/ui/Form';
import { EmptyState, InlineError } from '@/components/ui/Feedback';
import { cn } from '@/components/ui/cn';
import { formatDateTime, formatRelativeTime } from '@/lib/format';
import { useAddComment, useDeleteComment, useEditComment } from '@/lib/queries';

export interface ConversationProps {
  reference: number;
  comments: readonly TaskComment[];
  currentUser: UserSummary;
  canComment: boolean;
  canModerate: boolean;
}

/**
 * The task conversation (§11).
 *
 * Kept visually distinct from the rest of the page — its own surface, its own
 * rhythm — because the brief asks for it to read as a discussion thread rather
 * than another panel of fields.
 */
export function Conversation({ reference, comments, currentUser, canComment, canModerate }: ConversationProps) {
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const addComment = useAddComment(reference);

  const visible = comments.length;

  return (
    <div className="space-y-4">
      {visible === 0 ? (
        <EmptyState
          icon={<MessageSquare aria-hidden className="size-5" />}
          title="No messages yet"
          description="Ask a question, post an update, or leave context a director will need at review."
        />
      ) : (
        <ol className="space-y-4">
          {comments.map((comment) => (
            <li key={comment.id}>
              <CommentItem
                comment={comment}
                reference={reference}
                currentUser={currentUser}
                canModerate={canModerate}
                canComment={canComment}
                onReply={() => setReplyTo(comment.id)}
              />

              {comment.replies && comment.replies.length > 0 && (
                <ol className="mt-3 space-y-3 border-l-2 border-border pl-4 sm:ml-4">
                  {comment.replies.map((reply) => (
                    <li key={reply.id}>
                      <CommentItem
                        comment={reply}
                        reference={reference}
                        currentUser={currentUser}
                        canModerate={canModerate}
                        canComment={false}
                        isReply
                      />
                    </li>
                  ))}
                </ol>
              )}

              {replyTo === comment.id && canComment && (
                <div className="mt-3 sm:ml-4">
                  <CommentComposer
                    reference={reference}
                    parentId={comment.id}
                    placeholder={`Reply to ${comment.author.displayName}…`}
                    submitLabel="Post reply"
                    onDone={() => setReplyTo(null)}
                    onCancel={() => setReplyTo(null)}
                  />
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {canComment && replyTo === null && (
        <div className="border-t border-border pt-4">
          <CommentComposer reference={reference} parentId={null} placeholder="Write a message…" submitLabel="Post" />
        </div>
      )}

      {!canComment && (
        <p className="border-t border-border pt-4 text-xs text-text-subtle">
          This conversation is read-only for you.
        </p>
      )}

      <InlineError error={addComment.error} />
    </div>
  );
}

function CommentItem({
  comment,
  reference,
  currentUser,
  canModerate,
  canComment,
  isReply = false,
  onReply,
}: {
  comment: TaskComment;
  reference: number;
  currentUser: UserSummary;
  canModerate: boolean;
  canComment: boolean;
  isReply?: boolean;
  onReply?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);

  const editComment = useEditComment(reference);
  const deleteComment = useDeleteComment(reference);

  const isAuthor = comment.author.id === currentUser.id;
  const isDeleted = comment.deletedAt !== null;

  return (
    <article
      className={cn(
        'rounded-[--radius-panel] border border-border bg-surface p-3',
        isDeleted && 'opacity-70',
        isReply && 'bg-surface-sunken',
      )}
    >
      <header className="flex flex-wrap items-center gap-2">
        <Avatar user={comment.author} size="sm" />
        <span className="text-sm font-medium text-text">{comment.author.displayName}</span>
        <time
          dateTime={comment.createdAt}
          title={formatDateTime(comment.createdAt)}
          className="text-[11px] text-text-subtle"
        >
          {formatRelativeTime(comment.createdAt)}
        </time>
        {comment.editedAt && (
          <span className="text-[11px] text-text-subtle" title={formatDateTime(comment.editedAt)}>
            · edited
          </span>
        )}

        <span className="ml-auto flex items-center gap-0.5">
          {!isDeleted && canComment && onReply && (
            <IconButton label="Reply to this message" onClick={onReply}>
              <Reply aria-hidden className="size-3.5" />
            </IconButton>
          )}
          {!isDeleted && isAuthor && (
            <IconButton label="Edit your message" onClick={() => setEditing((value) => !value)}>
              <Pencil aria-hidden className="size-3.5" />
            </IconButton>
          )}
          {!isDeleted && (isAuthor || canModerate) && (
            <IconButton
              label="Delete this message"
              disabled={deleteComment.isPending}
              onClick={() => {
                if (window.confirm('Delete this message? Its replies will stay in place.')) {
                  deleteComment.mutate(comment.id);
                }
              }}
            >
              <Trash2 aria-hidden className="size-3.5" />
            </IconButton>
          )}
        </span>
      </header>

      {editing ? (
        <form
          className="mt-2.5 space-y-2"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            editComment.mutate(
              { commentId: comment.id, body: draft },
              { onSuccess: () => setEditing(false) },
            );
          }}
        >
          <TextArea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={LIMITS.commentMax}
            required
          />
          <InlineError error={editComment.error} />
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" variant="primary" loading={editComment.isPending}>
              Save changes
            </Button>
          </div>
        </form>
      ) : (
        <p
          className={cn(
            'mt-2 text-sm leading-relaxed whitespace-pre-wrap',
            isDeleted ? 'text-text-subtle italic' : 'text-text',
          )}
        >
          {comment.body}
        </p>
      )}

      {comment.attachments.length > 0 && (
        <ul className="mt-2.5 flex flex-wrap gap-2">
          {comment.attachments.map((attachment) => (
            <li key={attachment.id}>
              <a
                href={attachment.downloadUrl}
                className="inline-flex items-center gap-1.5 rounded border border-border bg-surface-sunken px-2 py-1 text-[11px] text-text-muted hover:text-accent-text"
              >
                {attachment.filename}
              </a>
            </li>
          ))}
        </ul>
      )}

      <InlineError error={deleteComment.error} className="mt-2" />
    </article>
  );
}

function CommentComposer({
  reference,
  parentId,
  placeholder,
  submitLabel,
  onDone,
  onCancel,
}: {
  reference: number;
  parentId: string | null;
  placeholder: string;
  submitLabel: string;
  onDone?: () => void;
  onCancel?: () => void;
}) {
  const [body, setBody] = useState('');
  const addComment = useAddComment(reference);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;

    addComment.mutate(
      { body: trimmed, parentId },
      {
        onSuccess: () => {
          setBody('');
          onDone?.();
        },
      },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <TextArea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder={placeholder}
        maxLength={LIMITS.commentMax}
        aria-label={placeholder}
      />
      <InlineError error={addComment.error} />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-text-subtle tabular">
          {body.length > LIMITS.commentMax - 500 ? `${body.length} / ${LIMITS.commentMax}` : ''}
        </span>
        <div className="flex gap-2">
          {onCancel && (
            <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            size="sm"
            variant="primary"
            loading={addComment.isPending}
            disabled={body.trim().length === 0}
            icon={<Send aria-hidden className="size-3.5" />}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}
